import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { computeCreateFolderMode, computeUploadMode } from '../../permissions/modes';
import { useFileManagerPermissions } from '../../permissions/useFileManagerPermissions';
import { flattenDataTransferItems, type FlattenedUploadEntry } from '../upload/uploadTreeFlattening';
import { notifyUploadBusy } from '../upload/uploadFolderTree';
import { useFileManagerUpload } from '../upload/useFileManagerUpload';

export const isNativeFileDrag = (event: DragEvent) =>
  event.dataTransfer?.types.includes('Files') ?? false;

// Folder upload genuinely creates folders, so it additionally needs files:createFolder. Without
// that permission, only the flat (non-nested) files in a drop are uploadable; nested entries are
// dropped. Pure so it can be unit-tested without the injected provider tree.
export const selectUploadableEntries = (
  entries: FlattenedUploadEntry[],
  canCreateFolders: boolean,
): FlattenedUploadEntry[] => {
  const hasNestedEntries = entries.some((entry) => entry.relativePath.includes('/'));
  if (hasNestedEntries && !canCreateFolders) {
    return entries.filter((entry) => !entry.relativePath.includes('/'));
  }
  return entries;
};

// Dropped folders are no longer filtered out (CESG-768): the tree-walking that used to be done
// inline here now lives in uploadTreeFlattening.ts's flattenDataTransferItems(), shared with the
// "Upload folder" picker input. The batch itself now runs through the shared
// useFileManagerUpload().runBatch() runner (also shared with the picker), so drops report through
// the same uploadItems progress panel and isUploading gating.
export const useNativeFileDropTarget = (path: MaybeRefOrGetter<string>) => {
  const { permissionsAt } = useFileManagerPermissions();
  // Scoped to this zone's own path, not the folder currently open: the zone may be a folder row in
  // another project's subtree, which carries its own capability set.
  const uploadMode = computed(() =>
    computeUploadMode(permissionsAt(toValue(path))),
  );
  const createFolderMode = computed(() =>
    computeCreateFolderMode(permissionsAt(toValue(path))),
  );
  const upload = useFileManagerUpload();
  const { nativeDragOverPath, isUploading } = upload;

  // Highlight is driven by the shared nativeDragOverPath (set below) rather than a per-instance ref
  // so only the innermost hovered zone lights up: the background zone (this path === currentPath)
  // yields to a folder the drag moves onto, matching the move drag's `dragOverPath` semantics.
  const isDragOver = computed(() => nativeDragOverPath.value === toValue(path));

  // Deliberately STRUCTURAL only — permission plus "is there a drive behind this path" — and
  // deliberately NOT `&& !isUploading`. This gate drives the highlight AND (via DropZone/the
  // onDragOver below) the cursor's dropEffect, so a zone that fails it refuses the drop outright and
  // no `drop` event ever fires on it. Folding the transient busy state in here would therefore make
  // the "an upload is already running" notification unreachable — the exact silent swallow it exists
  // to remove. Busy is checked in onDrop instead; see there.
  //
  // uploadMode is this entry point's permission gate; upload.canUploadTo(path) is the shared
  // "is there a drive behind this path" capability owned by the upload runner (mirrors
  // useDirectoryChildrenQuery's "not resolvable" query-disable guard - don't let a malformed request
  // hit Graph, just behave as if upload is unavailable: no drag-over highlight, drop no-ops). It is
  // asked about THIS zone's own path rather than currentPath, so an OS-file drop on the virtual root
  // - which lists document libraries, not a folder anyone can write into - neither highlights nor
  // uploads, while a drop on a folder inside a library still does.
  const canDrop = computed(
    () => uploadMode.value === 'visible' && upload.canUploadTo(toValue(path)),
  );

  // stopPropagation is what makes the innermost zone win: a folder's dragenter claims the shared
  // path and stops the event before the ancestor background zone can overwrite it back to its own.
  const onDragEnter = (event: DragEvent) => {
    if (!isNativeFileDrag(event)) return;
    event.stopPropagation();
    if (canDrop.value) nativeDragOverPath.value = toValue(path);
  };

  // What the OS cursor says over this zone during a file drag. `preventDefault()` is what accepts a
  // drop at all, and it must stay unconditional: FileManager.vue's root element prevents every
  // dragover anyway (so a stray drop never navigates the SPA away to the dropped file), which means
  // skipping it here would change nothing — the root would accept the drop a moment later while the
  // event bubbles.
  //
  // The honest signal is therefore dropEffect, not preventDefault: the zone writes it on the way up
  // and nothing above overwrites it. This is the same mechanism useDropTarget already relies on to
  // show a not-allowed cursor for an invalid internal drag. Without it, a zone with no drive behind
  // it (a virtual root, a namespace level) showed a "copy" cursor and then silently discarded
  // everything on release.
  const onDragOver = (event: DragEvent) => {
    event.preventDefault();
    // Only an OS file drag is this zone's business. An internal item drag reaching this handler has
    // bubbled up from a drop zone inside it (a grid row inside the pane-wide background zone) which
    // has already written the dropEffect its own transfer policy resolved to — overwriting that with
    // this zone's upload answer would replace a legitimate 'move' with 'copy' or 'none'. The bare
    // preventDefault() above is all such a drag ever wanted from an ancestor zone.
    if (!isNativeFileDrag(event)) return;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = canDrop.value ? 'copy' : 'none';
    }
  };

  const onDragLeave = (event: DragEvent) => {
    if (nativeDragOverPath.value !== toValue(path)) return;
    const related = event.relatedTarget as Node | null;
    const current = event.currentTarget as HTMLElement | null;
    if (related && current?.contains(related)) return;
    nativeDragOverPath.value = null;
  };

  const onDrop = async (event: DragEvent) => {
    if (!isNativeFileDrag(event)) return;
    event.stopPropagation();
    nativeDragOverPath.value = null;
    if (!canDrop.value || !event.dataTransfer) return;

    // Checked here rather than in canDrop (see its comment) and BEFORE flattening: the walk below
    // recursively reads the whole dropped tree through the Entries API and materializes every File,
    // which is seconds of work for a large folder. runBatch would refuse the batch anyway, so doing
    // that work first and then discarding it is pure waste.
    if (isUploading.value) {
      notifyUploadBusy();
      return;
    }

    const entries = await flattenDataTransferItems(Array.from(event.dataTransfer.items));
    const uploadableEntries = selectUploadableEntries(entries, createFolderMode.value === 'visible');
    if (!uploadableEntries.length) return;

    // Routes through the shared runner so drops inherit the same uploadItems progress panel and
    // isUploading gating as the "Skapa" picker (CESG-768).
    await upload.runBatch(uploadableEntries, toValue(path));
  };

  return { isDragOver, canDrop, onDragEnter, onDragOver, onDragLeave, onDrop };
};
